import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { WorkItem, Column, User, BoardWithCreator, LinkedBoardInfo, ChecklistItem } from 'api/todo/types';
import { getLinkedBoards } from 'api/todo/todoApi';
import updateCase from 'api/comments/updateCase';
import { CANtrackCase } from 'containers/pages/comments/types/comment';
import useCases from 'containers/pages/comments/hooks/useCases';
import { ImportTaskDialog } from './ImportTaskDialog';
import CANtrackSection, { CANtrackChangeData } from './CANtrackSection';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';

/**
 * Creates a minimal CANtrackCase object from task's case_id and case_title.
 * Used to populate the Autocomplete when editing a task with a linked case.
 */
const createCaseFromTask = (caseId: number, caseTitle: string): CANtrackCase => ({
  id: caseId,
  title: caseTitle,
  description: '',
  consultantID: 0,
  consultantName: '',
  customerName: '',
  customerID: 0,
  gpd: {
    group: { id: 0, title: '' },
    project: { id: 0, title: '' },
    deliverable: { id: 0, title: '' },
  },
});

interface TaskModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  task?: WorkItem;
  columns: Column[];
  users: User[];
  boards?: BoardWithCreator[];
  currentBoardId?: number;
  preselectedColumnId?: number | null;
  onClose: () => void;
  onSave: (title: string, description: string, columnId: number, assigneeIds: number[], caseId: number | null, caseTitle: string | null, dateDue: string | null, checklist: ChecklistItem[] | null) => void;
  onDelete?: () => void;
  onComplete?: (workItemId: number, doneColumnId: number) => void;
  onImport?: (workItemId: number, targetBoardId: number, targetColumnId: number) => Promise<void>;
  onLogToCANtrack?: (task: WorkItem) => void;
}

export const TaskModal: React.FC<TaskModalProps> = ({
  open,
  mode,
  task,
  columns,
  users,
  boards,
  currentBoardId,
  preselectedColumnId,
  onClose,
  onSave,
  onDelete,
  onComplete,
  onImport,
  onLogToCANtrack,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [columnId, setColumnId] = useState<number>(0);
  const [selectedAssignees, setSelectedAssignees] = useState<User[]>([]);
  const [selectedCase, setSelectedCase] = useState<CANtrackCase | null>(null);
  const [dateDue, setDateDue] = useState<Date | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [linkedBoards, setLinkedBoards] = useState<LinkedBoardInfo[]>([]);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const { loadingCases, cases, handleSearchChange } = useCases();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // CANtrack status state (reported from CANtrackSection)
  const [cantrackData, setCantrackData] = useState<CANtrackChangeData>({
    statusId: null,
    statusComment: '',
    hasChanges: false,
  });

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && task) {
        setTitle(task.title);
        setDescription(task.description || '');
        setColumnId(task.column_id);
        setSelectedAssignees(task.assignees || []);
        setSelectedCase(
          task.case_id && task.case_title
            ? createCaseFromTask(task.case_id, task.case_title)
            : null
        );
        setDateDue(task.date_due ? new Date(task.date_due) : null);
        setChecklist(task.checklist || []);
        // Fetch linked boards info
        getLinkedBoards(task.work_item_id)
          .then((data) => setLinkedBoards(data))
          .catch(() => setLinkedBoards([]));
      } else {
        setTitle('');
        setDescription('');
        const defaultColumnId = preselectedColumnId || (columns.length > 0 ? columns[0].column_id : 0);
        setColumnId(defaultColumnId);
        setSelectedAssignees([]);
        setSelectedCase(null);
        setDateDue(null);
        setChecklist([]);
        setLinkedBoards([]);
      }
      setCantrackData({ statusId: null, statusComment: '', hasChanges: false });
    } else {
      // Reset form when modal closes
      setTitle('');
      setDescription('');
      setColumnId(0);
      setSelectedAssignees([]);
      setSelectedCase(null);
      setDateDue(null);
      setChecklist([]);
      setLinkedBoards([]);
      setImportDialogOpen(false);
      setIsEditingTitle(false);
      setCantrackData({ statusId: null, statusComment: '', hasChanges: false });
    }
  }, [open, mode, task, columns, preselectedColumnId]);

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      return;
    }
    if (!columnId || columnId === 0) {
      return;
    }

    // Save CANtrack status changes if any
    if (cantrackData.hasChanges && selectedCase) {
      try {
        await updateCase({
          Id: selectedCase.id,
          ...(cantrackData.statusId !== null ? { StatusId: cantrackData.statusId } : {}),
          StatusComment: cantrackData.statusComment,
        });
      } catch (error) {
        console.error('[TaskModal] Failed to update case status:', error);
        // Don't block task save — continue
      }
    }

    const filteredChecklist = (checklist || []).filter((item) => item.text.trim() !== '');
    onSave(title.trim(), description.trim(), columnId, selectedAssignees.map((u) => u.user_id), selectedCase?.id || null, selectedCase?.title || null, dateDue ? dateDue.toISOString() : null, filteredChecklist.length > 0 ? filteredChecklist : null);
  };

  // Check if any field has changed from the original task (for edit mode)
  const hasChanges = (): boolean => {
    if (mode === 'create' || !task) return true;

    const originalAssigneeIds = (task.assignees || []).map((a) => a.user_id).sort();
    const currentAssigneeIds = selectedAssignees.map((a) => a.user_id).sort();
    const assigneesChanged =
      originalAssigneeIds.length !== currentAssigneeIds.length ||
      originalAssigneeIds.some((id, idx) => id !== currentAssigneeIds[idx]);

    const originalDue = task.date_due ? new Date(task.date_due).toDateString() : null;
    const currentDue = dateDue ? dateDue.toDateString() : null;

    const checklistChanged = JSON.stringify(checklist || []) !== JSON.stringify(task.checklist || []);

    return (
      title.trim() !== task.title ||
      description.trim() !== (task.description || '') ||
      columnId !== task.column_id ||
      assigneesChanged ||
      (selectedCase?.id || null) !== task.case_id ||
      currentDue !== originalDue ||
      checklistChanged ||
      cantrackData.hasChanges
    );
  };

  const handleClose = () => {
    if (hasChanges() && mode === 'edit') {
      setDiscardConfirmOpen(true);
    } else if (mode === 'create' && title.trim()) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const sortedColumns = [...columns].sort((a, b) => a.sort_order - b.sort_order);
  const doneColumnId = sortedColumns.length > 0 ? sortedColumns[sortedColumns.length - 1].column_id : null;
  const isAlreadyDone = doneColumnId !== null && columnId === doneColumnId;

  const isReference = task?.primary_work_item_id != null;
  const isPrimaryWithRefs = !isReference && linkedBoards.length > 1;
  // Other boards this task is linked to (excluding current board)
  const otherLinkedBoards = linkedBoards.filter((lb) => lb.board_id !== currentBoardId);

  const handleComplete = () => {
    if (mode === 'edit' && task && doneColumnId && onComplete) {
      onComplete(task.work_item_id, doneColumnId);
      onClose();
    }
  };

  const handleImport = async (targetBoardId: number, targetColumnId: number) => {
    if (task && onImport) {
      await onImport(task.work_item_id, targetBoardId, targetColumnId);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {mode === 'create' ? (
          'New Task'
        ) : isEditingTitle ? (
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setIsEditingTitle(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setIsEditingTitle(false);
              if (e.key === 'Escape') setIsEditingTitle(false);
            }}
            autoFocus
            fullWidth
            variant="standard"
            error={!title.trim()}
            InputProps={{
              sx: { fontSize: '1.25rem', fontWeight: 500 },
            }}
          />
        ) : (
          <Typography
            variant="h6"
            sx={{
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 },
              px: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={() => setIsEditingTitle(true)}
          >
            {title || 'Untitled'}
          </Typography>
        )}
        {isMobile && (
          <IconButton onClick={handleClose} edge="end" sx={{ ml: 1, flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {mode === 'create' && (
            <TextField
              label="Title"
              required
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!title.trim()}
            />
          )}
          <TextField
            label="Description"
            multiline
            minRows={isMobile ? 3 : 6}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{
              '& .MuiInputBase-inputMultiline': {
                resize: 'vertical',
              },
            }}
          />

          {/* Sub-Tasks */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Sub-Tasks {(checklist || []).length > 0 && `(${(checklist || []).filter((i) => i.completed).length}/${checklist.length})`}
            </Typography>
            {(checklist || []).map((item) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Checkbox
                  checked={item.completed}
                  size="small"
                  onChange={() => {
                    setChecklist((prev) =>
                      prev.map((ci) => (ci.id === item.id ? { ...ci, completed: !ci.completed } : ci))
                    );
                  }}
                />
                <TextField
                  value={item.text}
                  variant="standard"
                  size="small"
                  fullWidth
                  placeholder="Enter item..."
                  autoFocus={item.text === ''}
                  onChange={(e) => {
                    setChecklist((prev) =>
                      prev.map((ci) => (ci.id === item.id ? { ...ci, text: e.target.value } : ci))
                    );
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && item.text.trim() !== '') {
                      e.preventDefault();
                      setChecklist((prev) => [
                        ...prev,
                        { id: Math.random().toString(36).slice(2, 8), text: '', completed: false },
                      ]);
                    }
                  }}
                  InputProps={{
                    sx: {
                      textDecoration: item.completed ? 'line-through' : 'none',
                      color: item.completed ? 'text.secondary' : 'text.primary',
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setChecklist((prev) => prev.filter((ci) => ci.id !== item.id))}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setChecklist((prev) => [
                  ...prev,
                  { id: Math.random().toString(36).slice(2, 8), text: '', completed: false },
                ])
              }
              sx={{ textTransform: 'none', mt: 0.5 }}
            >
              Add item
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={columnId}
                label="Status"
                onChange={(e) => setColumnId(e.target.value as number)}
              >
                {sortedColumns.map((col) => (
                  <MenuItem key={col.column_id} value={col.column_id}>
                    {col.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {mode === 'edit' && !isAlreadyDone && doneColumnId && onComplete && (
              <Tooltip title="Mark as complete (move to last column)">
                <IconButton
                  size="small"
                  color="success"
                  onClick={handleComplete}
                  sx={{
                    mt: 1,
                    border: '1px solid',
                    borderColor: 'success.main',
                    '&:hover': {
                      backgroundColor: 'success.main',
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <CheckCircle sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <MobileDatePicker
                label="Due Date (optional)"
                value={dateDue}
                onChange={(newValue: Date | null) => setDateDue(newValue)}
                renderInput={(params: any) => <TextField {...params} fullWidth />}
              />
              {dateDue && (
                <Tooltip title="Clear due date">
                  <IconButton
                    size="small"
                    onClick={() => setDateDue(null)}
                    sx={{ mt: 1 }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </LocalizationProvider>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Autocomplete
              multiple
              disableClearable
              options={users}
              getOptionLabel={(option) => option.name}
              value={selectedAssignees}
              onChange={(_, newValue) => setSelectedAssignees(newValue)}
              sx={{ flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Assignees" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.name} {...getTagProps({ index })} key={option.user_id} />
                ))
              }
            />
            {selectedAssignees.length > 0 && (
              <Tooltip title="Clear assignees">
                <IconButton
                  size="small"
                  onClick={() => setSelectedAssignees([])}
                  sx={{ mt: 1 }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Linked boards info */}
          {mode === 'edit' && otherLinkedBoards.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <LinkIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Also on:
              </Typography>
              {otherLinkedBoards.map((lb) => (
                <Chip
                  key={lb.board_id}
                  label={lb.board_name}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          )}

          {/* CANtrack Section */}
          <CANtrackSection
            cases={cases}
            loadingCases={loadingCases}
            selectedCase={selectedCase}
            onCaseChange={(newCase) => setSelectedCase(newCase)}
            onSearchChange={handleSearchChange}
            onLogComment={() => task && onLogToCANtrack?.(task)}
            onCANtrackChange={setCantrackData}
          />

        </Box>
      </DialogContent>
      <DialogActions>
        {mode === 'edit' && onDelete && (
          <Button onClick={onDelete} color="error" sx={{ mr: 'auto', minHeight: { xs: 44, sm: 36 } }}>
            {isReference ? 'Remove from Board' : isPrimaryWithRefs ? 'Delete from All Boards' : 'Delete'}
          </Button>
        )}
        {mode === 'edit' && onImport && boards && currentBoardId && (
          <Tooltip title="Import this task to another board">
            <Button
              onClick={() => setImportDialogOpen(true)}
              startIcon={<CallSplitIcon />}
              sx={{ minHeight: { xs: 44, sm: 36 } }}
            >
              Import to Board
            </Button>
          </Tooltip>
        )}
        <Button onClick={handleClose} sx={{ minHeight: { xs: 44, sm: 36 } }}>Back</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!title.trim() || !columnId || (mode === 'edit' && !hasChanges())}
          sx={{ minHeight: { xs: 44, sm: 36 } }}
        >
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>

      {/* Import dialog */}
      {boards && currentBoardId && (
        <ImportTaskDialog
          open={importDialogOpen}
          boards={boards}
          currentBoardId={currentBoardId}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleImport}
        />
      )}

      {/* Unsaved changes confirmation */}
      <ConfirmationDialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard"
        cancelText="Keep Editing"
      />
    </Dialog>
  );
};
